// src/Chat/MessageList.jsx
import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useState,
  useContext,
} from 'react';
import {
  Box,
  Typography,
  Paper,
  Fab,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import axios from 'axios';
import MessageAttachment from './MessageAttachment';
import { useChatContext } from '../context/SignamRContext';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { TabContext } from '../Template/Context/TabContext';
import { useTranslation } from 'react-i18next';
import ReplyIcon from '@mui/icons-material/Reply';

const WA_GREEN = '#ff8f28';
const BUBBLE_ME = '#d9fdd3';
const BUBBLE_THEM = '#ffffff';
const SEPARATOR_BG = 'rgba(0,0,0,0.06)';
const PAGE_SIZE = 15;
const DELETED_MESSAGE_TEXT = 'Ova poruka je obrisana';
const DOKUMENT_ID = 2;
const TESTIRANJE_ID = 1002;

// ===== helpers =====
const formatTimeHM = (dt) => {
  if (!dt) return '';
  const d =
    typeof dt === 'string' || typeof dt === 'number' ? new Date(dt) : dt;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
const dayLabel = (date, danas, juce) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (+d === +today) return danas;
  if (+d === +yesterday) return juce;
  return d.toLocaleDateString();
};
const parseSeenIds = (seen) => {
  if (Array.isArray(seen)) return seen;
  if (typeof seen === 'string') {
    try {
      return JSON.parse(seen);
    } catch {
      return [];
    }
  }
  return [];
};
const isNearBottom = (el, threshold = 200) =>
  el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

// ===== Message bubble =====
const MessageItem = React.memo(
  ({
    msg,
    isMine,
    isReadByAll,
    showName,
    onShowOptions,
    handleOpenPopUp,
    modifiedMsg,
  }) => {
    const timeStr = formatTimeHM(msg?.createdAt);
    const hasBody = !!msg?.body;
    const hasImage = !!msg?.image;
    const isDeletedMessage = (msg?.body || '').trim() === DELETED_MESSAGE_TEXT;
    const safeOznaka =
      msg?.oznaka && msg.oznaka !== 'null' && msg.oznaka !== 'undefined'
        ? msg.oznaka
        : null;
    const hasOznaka = !!safeOznaka;
    const timerRef = useRef(null);

    const handleMouseDown = (e) => {
      if (isDeletedMessage) return;
      timerRef.current = setTimeout(() => {
        onShowOptions(e, msg); // long press trigger
      }, 500);
    };

    const handleMouseUp = () => {
      clearTimeout(timerRef.current);
    };
    const handleClickOznaka = () => {
      handleOpenPopUp(msg);
    };
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isMine ? 'flex-end' : 'flex-start',
          mb: 0.75,
          px: 0.5,
          // sprečimo da poruka postane anchor
          overflowAnchor: 'none',
        }}
      >
        <Box
          sx={{
            maxWidth: '80%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMine ? 'flex-end' : 'flex-start',
          }}
        >
          {!isMine && showName && (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.72rem', color: '#667781', mb: 0.25, ml: 0.5 }}
            >
              {msg.name || msg.sender?.name || msg.user?.name || 'Nepoznat'}
            </Typography>
          )}

          <Paper
            elevation={0}
            onContextMenu={
              isDeletedMessage ? undefined : (e) => onShowOptions(e, msg)
            } // desktop right click
            onMouseDown={isDeletedMessage ? undefined : handleMouseDown}
            onMouseUp={isDeletedMessage ? undefined : handleMouseUp}
            onMouseLeave={isDeletedMessage ? undefined : handleMouseUp}
            onTouchStart={isDeletedMessage ? undefined : handleMouseDown}
            onTouchEnd={isDeletedMessage ? undefined : handleMouseUp}
            sx={{
              position: 'relative',
              p: 1,
              px: 1.25,
              pr: 5, // prostor za vreme/tick
              pb: hasOznaka ? 3 : 2, // više mesta kad ima oznaku
              bgcolor: isMine ? BUBBLE_ME : BUBBLE_THEM,
              borderRadius: isMine
                ? '12px 12px 2px 12px'
                : '12px 12px 12px 2px',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              border: '1px solid rgba(0,0,0,0.06)',
              minWidth: hasBody ? 160 : 120, // minimalna širina poruke
              '::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                [isMine ? 'right' : 'left']: -6,
                width: 12,
                height: 12,
                bgcolor: isMine ? BUBBLE_ME : BUBBLE_THEM,
                transform: 'translateY(-1px) rotate(45deg)',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                borderRight: isMine ? '1px solid rgba(0,0,0,0.06)' : 'none',
                borderLeft: !isMine ? '1px solid rgba(0,0,0,0.06)' : 'none',
                zIndex: 0,
              },
            }}
          >
            {msg.parentBody && (
              <Box
                sx={{
                  borderLeft: '2px solid #ff8f28',
                  pl: 1,
                  mb: 0.5,
                  bgcolor: 'rgba(255,143,40,0.05)',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.75rem', color: '#555' }}
                >
                  {msg.parentBody.length > 80
                    ? `${msg.parentBody.slice(0, 80)}...`
                    : msg.parentBody}
                </Typography>
              </Box>
            )}
            {hasBody && (
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.4,
                  fontStyle: isDeletedMessage ? 'italic' : 'normal',
                  color: isDeletedMessage ? 'rgba(0,0,0,0.55)' : 'inherit',
                }}
              >
                {msg.body}
              </Typography>
            )}
            {hasImage && (
              <Box sx={{ mt: hasBody ? 1 : 0 }}>
                <MessageAttachment fileUrl={msg.image} />
              </Box>
            )}

            {/* vreme / tick / oznaka u dnu desno */}
            <Box
              sx={{
                position: 'absolute',
                right: 6,
                bottom: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 0.25,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isMine && (
                  <DoneAllIcon
                    sx={{
                      fontSize: 18,
                      color: isReadByAll ? '#53bdeb' : 'rgba(0,0,0,0.38)',
                    }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#667781', fontSize: '0.72rem' }}
                >
                  {timeStr}
                </Typography>

                {msg.izmena && !isDeletedMessage && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#999',
                      fontSize: '0.72rem',
                      fontStyle: 'italic',
                    }}
                  >
                    {modifiedMsg}
                  </Typography>
                )}

                {hasOznaka && (
                  <Box
                    component="span"
                    sx={{
                      bgcolor: WA_GREEN,
                      color: '#fff',
                      borderRadius: '999px',
                      px: 0.8,
                      py: 0.1,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                    onClick={isMine ? handleClickOznaka : () => {}}
                  >
                    {safeOznaka}
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  },
);
const WA_GREEN_HOVER = '#fd8412ff';
// ===== Main list =====
const MessageList = ({
  isEditMsg,
  setIsEditMsg,
  msgForEdit,
  setMsgForEdit,
  setActiveChat,
  replyTo,
  setReplyTo,
}) => {
  const {
    messages,
    setMessages,
    searchMessageId,
    setSearchMessageId,
    setConversationId,
  } = useChatContext();
  const navigate = useNavigate();
  const myUserId = +localStorage.getItem('idKorisnika');
  const messageRefs = useRef({});
  const containerRef = useRef(null);
  const topSentinelRef = useRef(null);
  const ioRef = useRef(null);
  const { t, i18n } = useTranslation();
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const loadingOlderRef = useRef(false);
  const prevLenRef = useRef(0);

  const [showScrollDown, setShowScrollDown] = useState(false);

  const [contextMenu, setContextMenu] = useState({
    mouseX: null,
    mouseY: null,
    open: false,
    item: null,
  });
  const [tabsArr, setTabsArr] = useState([]);
  const [activeHistoryArr, setActiveHistoryArr] = useState([]);
  const [tabsData, setTabsData] = useState([]); // dodato
  const [activeComponent, setActiveComponent] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_CHAT_PROD;

  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedOznaka, setSelectedOznaka] = useState(null);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [catLoading, setCatLoading] = useState(false);
  const [catError, setCatError] = useState('');

  const [docTags, setDocTags] = useState([]);
  const [selectedDocTag, setSelectedDocTag] = useState('');
  const [docTagsLoading, setDocTagsLoading] = useState(false);
  const [docTagsError, setDocTagsError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');
  const [openDeleteMessageDialog, setOpenDeleteMessageDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const { addTab } = useContext(TabContext);

  const isDokument = Number(selectedCategory) === DOKUMENT_ID;

  const handleOpenPopUp = (msg) => {
    setSelectedOznaka(msg.oznaka);
    setEditedMessage(msg);
    setPopupOpen(true);
  };

  const handleClosePopUp = () => {
    setPopupOpen(false);
    setSelectedOznaka(null);
  };

  useEffect(() => {
    if (!popupOpen) return;

    const fetchCategories = async () => {
      setCatLoading(true);
      setCatError('');
      try {
        const { data } = await axios.post(
          `${apiBaseUrl}/Combo/vrati_kategorije_combo_novi`,
          { idKorProm: myUserId },
        );
        setCategories(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) {
          setSelectedCategory(data[0].id);
        }
      } catch {
        setCatError('Greška pri učitavanju kategorija.');
      } finally {
        setCatLoading(false);
      }
    };

    fetchCategories();
  }, [popupOpen, apiBaseUrl, myUserId]);

  useEffect(() => {
    const idMasine = messages?.idMasine; // ili activeChat?.idMasine ako ga imaš
    const isDokument = true; // možeš uslov da postaviš prema tipu

    if (!popupOpen || !isDokument || !idMasine) {
      setDocTags([]);
      setSelectedDocTag('');
      setDocTagsLoading(false);
      setDocTagsError('');
      return;
    }

    const fetchDocTags = async () => {
      setDocTagsLoading(true);
      setDocTagsError('');
      try {
        const response = await axios.post(
          `${apiBaseUrl}/Conversations/vrati_oznake_dokumenta`,
          { id: idMasine },
        );
        setDocTags(Array.isArray(response.data) ? response.data : []);
      } catch {
        setDocTagsError('Greška pri učitavanju oznaka dokumenta.');
      } finally {
        setDocTagsLoading(false);
      }
    };

    fetchDocTags();
  }, [selectedCategory, popupOpen, messages, apiBaseUrl]);

  const showOptions = (event, item) => {
    if ((item?.body || '').trim() === DELETED_MESSAGE_TEXT) {
      return;
    }
    event.preventDefault();

    let mouseX;
    let mouseY;

    if (event.clientX && event.clientY) {
      // desktop (right click ili long press na mišu)
      mouseX = event.clientX;
      mouseY = event.clientY;
    } else {
      // touch devices
      const rect = event.target.getBoundingClientRect();
      mouseX = rect.left + rect.width / 2;
      mouseY = rect.top + rect.height / 2;
    }

    setContextMenu({
      mouseX,
      mouseY,
      open: true,
      item,
    });
  };

  const handleCloseMenu = () => {
    setContextMenu({ ...contextMenu, open: false, item: null });
  };
  const handleConfirm = async () => {
    console.log(editedMessage);
    console.log(selectedCategory);
    console.log(selectedDocTag);

    const trimmed = editedMessage.body.trim();
    if (!trimmed) return;

    const endpoint = `${apiBaseUrl}/Messages/update_message`;

    const isTestiranje = Number(selectedCategory) === TESTIRANJE_ID;
    const payload = {
      conversationId: editedMessage?.conversationId,
      message: trimmed,
      messageId: editedMessage.id,
      userId: +localStorage.getItem('idKorisnika'),
      id_kategorije: +localStorage.getItem('id_kategorije'),
      id_dokumenta: +localStorage.getItem('id_dokumenta'),
      idKorisnika: +localStorage.getItem('idKorisnika'),
      oznaka: isTestiranje ? 'TESTIRANJE' : selectedDocTag || 'null',
    };

    try {
      const response = await axios.post(endpoint, payload);
      if (response.status === 200) {
        setEditedMessage(null);
        setSelectedCategory(null);
        setSelectedDocTag(null);
        setSelectedOznaka(null);

        setPopupOpen(false);
      }
    } catch (err) {
      console.error(
        `Greška prilikom ${isEditMsg ? 'izmene' : 'slanja'} poruke:`,
        err,
      );
    }
  };

  const handleNewSZ = (item) => {
    console.log(item);
    const tab = {
      title: t('newServiceRequest'),
      icon: 'fas fa-toolbox',
      type: 10010,
      data: {
        idMasine: messages.idMasine,
        qr: true,
        fromChat: true,
        opisProblema: item.body,
      },
    };
    addTab(tab);
    // navigate('/');
    // setActiveChat(null);
    // setConversationId(null);
  };

  const handleEditMsg = (item) => {
    console.log(item);
    setIsEditMsg(true);
    setMsgForEdit(item);
  };

  const handleOpenDeleteMessageDialog = (item) => {
    setMessageToDelete(item);
    setOpenDeleteMessageDialog(true);
  };

  const handleDeleteMessage = async () => {
    if (!messageToDelete?.id) return;

    try {
      const payload = {
        conversationId: messageToDelete.conversationId || messages?.id,
        message: messageToDelete.body || '',
        messageId: messageToDelete.id,
        userId: +localStorage.getItem('idKorisnika'),
        id_kategorije: +localStorage.getItem('id_kategorije'),
        id_dokumenta: +localStorage.getItem('id_dokumenta'),
        idKorisnika: +localStorage.getItem('idKorisnika'),
        oznaka: messageToDelete.oznaka || 'null',
      };

      const response = await axios.post(
        `${apiBaseUrl}/Messages/update_message_delete`,
        payload,
      );

      if (response.status === 200) {
        const responseData = response.data;
        const responseMessages = Array.isArray(responseData?.messages)
          ? responseData.messages
          : [];
        const deletedMessageFromResponse = Array.isArray(responseData)
          ? responseData[responseData.length - 1]
          : responseData?.lastMessage ||
            responseData?.message ||
            (responseMessages.length
              ? responseMessages[responseMessages.length - 1]
              : null) ||
            responseData;

        setMessages((prev) => {
          const prevMessages = Array.isArray(prev?.messages)
            ? prev.messages
            : [];
          if (
            deletedMessageFromResponse &&
            typeof deletedMessageFromResponse === 'object'
          ) {
            const targetId =
              deletedMessageFromResponse.id ||
              deletedMessageFromResponse.messageId;
            const updatedMessage = {
              ...messageToDelete,
              ...deletedMessageFromResponse,
            };

            // Poruka mora ostati na istoj poziciji u listi, samo se menja sadržaj.
            if (targetId) {
              return {
                ...prev,
                messages: prevMessages.map((msg) =>
                  msg.id === targetId ? updatedMessage : msg,
                ),
              };
            }

            return {
              ...prev,
              messages: prevMessages.map((msg) =>
                msg.id === messageToDelete.id ? updatedMessage : msg,
              ),
            };
          }

          return {
            ...prev,
            messages: prevMessages.map((msg) =>
              msg.id === messageToDelete.id
                ? { ...msg, body: DELETED_MESSAGE_TEXT, izmena: false }
                : msg,
            ),
          };
        });
      }
    } catch (error) {
      console.error('Greška pri brisanju poruke:', error);
    } finally {
      setOpenDeleteMessageDialog(false);
      setMessageToDelete(null);
    }
  };

  // grupisanje po danima (UI)
  const groups = useMemo(() => {
    const list = Array.isArray(messages?.messages) ? messages.messages : [];
    const byDay = [];
    let current = null;
    const today = t('today');
    const yestrday = t('yestrday');
    list.forEach((m) => {
      const d = new Date(m.createdAt);
      const label = dayLabel(d, today, yestrday);
      if (!current || current.label !== label) {
        current = { label, items: [] };
        byDay.push(current);
      }
      current.items.push(m);
    });
    return byDay;
  }, [messages?.messages]);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = containerRef.current;
    if (!el) return;
    const top = el.scrollHeight;
    if (behavior === 'smooth') {
      el.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollTop = top;
    }
  }, []);

  // Učitavanje starijih (samo kada korisnik stigne na vrh)
  const loadMore = useCallback(async () => {
    const el = containerRef.current;
    const convId = messages?.messages?.[0]?.conversationId;
    if (!el || isLoadingMoreRef.current || !hasMoreRef.current || !convId)
      return;

    try {
      isLoadingMoreRef.current = true;
      loadingOlderRef.current = true;

      const offset = messages?.messages?.length || 0;
      const prevHeight = el.scrollHeight;

      const res = await axios.post(
        `${process.env.REACT_APP_API_CHAT_PROD}/Conversations/getConversationById`,
        {
          conversationId: convId,
          userId: +localStorage.getItem('idKorisnika'),
          offset,
          next: PAGE_SIZE,
        },
      );

      const batch = Array.isArray(res?.data?.messages) ? res.data.messages : [];
      if (batch.length === 0) {
        hasMoreRef.current = false;
        return;
      }
      if (batch.length < PAGE_SIZE) {
        hasMoreRef.current = false;
      }

      setMessages((prev) => ({
        ...prev,
        messages: [...batch, ...(prev?.messages || [])], // prepend
      }));

      // zadrži relativnu poziciju skrola
      requestAnimationFrame(() => {
        const newHeight = el.scrollHeight;
        el.scrollTop = newHeight - prevHeight;
      });
    } catch (err) {
      console.error('Greška pri učitavanju poruka:', err);
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [messages?.messages, setMessages]);

  // Pri ulasku u razgovor: sačekaj farbanje i „zakucaj“ dno (da ne skakuće)
  useLayoutEffect(() => {
    const el = containerRef.current;
    const convId = messages?.messages?.[0]?.conversationId;
    if (!el || !convId) {
      return () => {};
    }

    prevLenRef.current = Array.isArray(messages?.messages)
      ? messages.messages.length
      : 0;
    hasMoreRef.current = true;

    const goBottom = () => {
      if (!searchMessageId) {
        el.scrollTop = el.scrollHeight;
      }
    };

    // 2× rAF + dva kratka delay-a — hvata i kasni reflow (thumbnaile/slike)
    const r1 = requestAnimationFrame(() => requestAnimationFrame(goBottom));
    const t1 = setTimeout(goBottom, 0);
    const t2 = setTimeout(goBottom, 300);

    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [messages?.messages?.[0]?.conversationId]);

  // IO za TOP sentinel (pokreće loadMore kad baš priđeš vrhu)
  useEffect(() => {
    const root = containerRef.current;
    const target = topSentinelRef.current;
    if (!root || !target) {
      return () => {};
    }

    // počisti prethodni observer
    if (ioRef.current) {
      ioRef.current.disconnect();
      ioRef.current = null;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // dodatni uslov: stvarno si na vrhu
        const atVeryTop = root.scrollTop <= 40;
        if (
          entry.isIntersecting &&
          atVeryTop &&
          !isLoadingMoreRef.current &&
          hasMoreRef.current &&
          messages?.messages?.[0]?.conversationId &&
          !searchMessageId
        ) {
          loadMore().catch(() => {});
        }
      },
      { root, rootMargin: '0px', threshold: 0 },
    );

    io.observe(target);
    ioRef.current = io;

    return () => {
      io.disconnect();
    };
  }, [loadMore, messages?.messages?.[0]?.conversationId]);

  // Nove poruke → dno samo ako si blizu dna ili je poruka tvoja
  useEffect(() => {
    if (searchMessageId) return () => {};
    const el = containerRef.current;
    if (!el) return () => {};
    const list = Array.isArray(messages?.messages) ? messages.messages : [];
    const currLen = list.length;
    const prevLen = prevLenRef.current;
    if (loadingOlderRef.current) {
      loadingOlderRef.current = false;
      prevLenRef.current = currLen;
      return () => {};
    }
    if (currLen > prevLen) {
      const last = list[currLen - 1];
      const isMine = String(last.senderId) === String(myUserId);
      if (isNearBottom(el) || isMine) {
        scrollToBottom('smooth');
      }
    }
    prevLenRef.current = currLen;
    return () => {};
  }, [messages?.messages, myUserId, scrollToBottom]);

  // onScroll: toggle dugme + loadMore fallback na samom vrhu
  const handleScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const nearBottom = isNearBottom(el, 120);
      setShowScrollDown(!nearBottom);

      if (
        el.scrollTop <= 40 &&
        !isLoadingMoreRef.current &&
        hasMoreRef.current
      ) {
        loadMore().catch(() => {});
      }
    },
    [loadMore],
  );

  const scrollToMessage = useCallback((id) => {
    const el = messageRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.firstChild.firstChild.style.outline = '4px solid #ff8f28';
      setTimeout(() => {
        el.firstChild.firstChild.style.outline = 'none';
      }, 2000);
    } else {
      console.warn('Nema elementa za id', id);
    }
  }, []);

  useEffect(() => {
    if (searchMessageId) {
      scrollToMessage(searchMessageId);
      setSearchMessageId(null); // reset odmah posle skrola
    }
  }, [searchMessageId, scrollToMessage, setSearchMessageId]);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        p: 1,
        pt: 1,
        overflowY: 'auto',
        flex: 1,
        position: 'relative',
        height: '45vh',
        // ostavljamo default overflow anchoring (NE gasimo ga)
      }}
    >
      {/* TOP sentinel — IO ga posmatra */}
      <div ref={topSentinelRef} style={{ height: 1 }} />

      {groups.map((g) => (
        <Box key={g.label} sx={{ mb: 1.5, overflowAnchor: 'none' }}>
          {/* separator dana */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
            <Box
              sx={{
                px: 1.25,
                py: 0.3,
                bgcolor: SEPARATOR_BG,
                borderRadius: '999px',
                fontSize: '0.75rem',
                color: '#54656f',
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              {g.label}
            </Box>
          </Box>

          {g.items.map((msg) => {
            const isMine = String(msg.senderId) === String(myUserId);
            const seenIds = parseSeenIds(msg.seenIds);
            const allOtherUsers =
              messages?.users
                ?.filter((u) => u.user?.id !== myUserId)
                ?.map((u) => u.user?.id) || [];
            const isReadByAll =
              isMine &&
              allOtherUsers.length > 0 &&
              allOtherUsers.every((id) => seenIds.includes(id));
            const showName = !isMine && (messages?.users?.length || 0) > 2;

            return (
              <div
                key={msg.id}
                ref={(el) => {
                  if (el) messageRefs.current[msg.id] = el;
                }}
              >
                <MessageItem
                  msg={msg}
                  isMine={isMine}
                  isReadByAll={isReadByAll}
                  showName={showName}
                  onShowOptions={showOptions}
                  handleOpenPopUp={handleOpenPopUp}
                  modifiedMsg={t('edited')}
                />
              </div>
            );
          })}
        </Box>
      ))}

      {/* DNO LISTE — bottom sentinel drži anchor na dnu */}
      <div style={{ height: 1, overflowAnchor: 'auto' }} />

      {/* Scroll-down FAB (overlay) */}
      {showScrollDown && (
        <Box
          // sticky kontejner drži FAB iznad dna scroll-view-a
          sx={{
            position: 'sticky',
            bottom: (theme) => `calc(${theme.spacing(2)} + 1px)`, // ≈ visina inputa + mali razmak
            display: 'flex',
            justifyContent: 'flex-end',
            pr: 1.5,
            pointerEvents: 'none', // da ne blokira skrol
          }}
        >
          <Fab
            size="small"
            onClick={() => scrollToBottom('smooth')}
            sx={{
              pointerEvents: 'auto', // klik radi normalno
              bgcolor: '#fff',
              color: WA_GREEN,
              border: '1px solid rgba(0,0,0,0.12)',
              boxShadow: 3,
              '&:hover': { bgcolor: '#fff' },
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 18,
                color: WA_GREEN,
              }}
            />
          </Fab>
        </Box>
      )}
      <Menu
        open={contextMenu.open}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.mouseY !== null && contextMenu.mouseX !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            handleNewSZ(contextMenu.item);
            handleCloseMenu();
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          {t('newServiceRequest')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setReplyTo(contextMenu.item);
            handleCloseMenu();
          }}
        >
          <ReplyIcon fontSize="small" sx={{ mr: 1 }} />
          {t('replyMessage')}
        </MenuItem>

        {String(contextMenu.item?.senderId) ===
          String(localStorage.getItem('idKorisnika')) && (
          <>
            <MenuItem
              onClick={() => {
                handleEditMsg(contextMenu.item);
                handleCloseMenu();
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              {t('editMessage')}
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleOpenPopUp(contextMenu.item);
                handleCloseMenu();
              }}
            >
              <BookmarkIcon fontSize="small" sx={{ mr: 1 }} />
              {t('addMark')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleOpenDeleteMessageDialog(contextMenu.item);
                handleCloseMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
              Obriši poruku
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog
        open={popupOpen}
        onClose={handleClosePopUp}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            {t('pickCategory')}
          </Typography>

          {/* Kategorija */}
          <FormControl
            fullWidth
            variant="outlined"
            margin="normal"
            disabled={catLoading}
            error={!!catError}
          >
            <InputLabel id="kategorija-label" shrink>
              {t('category')}
            </InputLabel>
            <Select
              labelId="kategorija-label"
              id="kategorija-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Kategorija"
              displayEmpty
              sx={{ height: 56, '& .MuiSelect-select': { py: 1.5 } }}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.vrednost}
                </MenuItem>
              ))}
            </Select>
            {!!catError && <FormHelperText error>{catError}</FormHelperText>}
          </FormControl>

          {catLoading && (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Učitavanje kategorija…</Typography>
            </Box>
          )}

          {/* Oznake dokumenta – samo za "Dokument" */}
          {isDokument && (
            <>
              <FormControl
                fullWidth
                variant="outlined"
                margin="normal"
                disabled={
                  docTagsLoading || !!docTagsError || docTags.length === 0
                }
              >
                <InputLabel id="oznaka-doc-label" shrink>
                  {t('markDocument')}
                </InputLabel>
                <Select
                  labelId="oznaka-doc-label"
                  id="oznaka-doc-select"
                  value={selectedDocTag}
                  onChange={(e) => setSelectedDocTag(e.target.value)}
                  label={t('markDocument')}
                  displayEmpty
                  sx={{ height: 56, '& .MuiSelect-select': { py: 1.5 } }}
                >
                  {docTags.map((t) => (
                    <MenuItem key={t.id} value={t.vrednost}>
                      {t.vrednost}
                    </MenuItem>
                  ))}
                </Select>

                {!!docTagsError && (
                  <FormHelperText error>{docTagsError}</FormHelperText>
                )}
                {!docTagsError && docTags.length === 0 && !docTagsLoading && (
                  <FormHelperText>{t('noAvailableMark')}</FormHelperText>
                )}
              </FormControl>

              {docTagsLoading && (
                <Box mt={1} display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Učitavanje oznaka…</Typography>
                </Box>
              )}
            </>
          )}

          {/* Submit greška */}
          {submitError && (
            <Typography variant="body2" color="error" mt={1}>
              {submitError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              bgcolor: WA_GREEN,
              '&:hover': { bgcolor: WA_GREEN_HOVER },
              fontWeight: 600,
            }}
            onClick={handleConfirm}
            disabled={
              isSubmitting ||
              !selectedCategory ||
              (isDokument && !selectedDocTag)
            }
            startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
          >
            {isSubmitting ? 'Slanje…' : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteMessageDialog}
        onClose={() => setOpenDeleteMessageDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography variant="subtitle1" fontWeight={600}>
            Da li ste sigurni da želite da obrišete poruku?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenDeleteMessageDialog(false)}
            sx={{
              color: WA_GREEN,
              borderColor: WA_GREEN,
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 143, 40, 0.08)',
                borderColor: WA_GREEN,
              },
            }}
          >
            Otkaži
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteMessage}
            sx={{
              bgcolor: '#d32f2f',
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            Obriši
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageList;
